import { useState, useCallback } from 'react';

const useCommitteeEdit = () => {
  const [editingCommitteeId, setEditingCommitteeId] = useState(null);
  const [dirtyCommitteeIds, setDirtyCommitteeIds] = useState(new Set());
  const [pendingEditCommitteeId, setPendingEditCommitteeId] = useState(null);
  const [showSwitchWarningModal, setShowSwitchWarningModal] = useState(false);

  /**
   * Called by a ChairReport when the user clicks Edit/Lock.
   * If another form has unsaved changes, show a warning modal.
   * Otherwise, switch editing to the requested committee (or lock if same).
   */
  const handleRequestEdit = useCallback(
    (committeeId) => {
      // Toggling off the currently-editing form (Lock)
      if (editingCommitteeId === committeeId) {
        setEditingCommitteeId(null);
        return;
      }

      // Switching to a new form — check if current has unsaved changes
      if (
        editingCommitteeId !== null &&
        dirtyCommitteeIds.has(editingCommitteeId)
      ) {
        setPendingEditCommitteeId(committeeId);
        setShowSwitchWarningModal(true);
        return;
      }

      // No conflict — switch immediately
      setEditingCommitteeId(committeeId);
    },
    [editingCommitteeId, dirtyCommitteeIds],
  );

  /** Confirm switching away from a dirty form (discard changes). */
  const handleConfirmSwitch = useCallback(() => {
    setShowSwitchWarningModal(false);
    setDirtyCommitteeIds((prev) => {
      const next = new Set(prev);
      next.delete(editingCommitteeId);
      return next;
    });
    setEditingCommitteeId(pendingEditCommitteeId);
    setPendingEditCommitteeId(null);
  }, [editingCommitteeId, pendingEditCommitteeId]);

  /** Cancel the switch — keep editing the current form. */
  const handleCancelSwitch = useCallback(() => {
    setShowSwitchWarningModal(false);
    setPendingEditCommitteeId(null);
  }, []);

  /**
   * Called by each ChairReport when its dirty state changes,
   * so the parent can track which forms have unsaved work.
   */
  const handleDirtyChange = useCallback((committeeId, isDirty) => {
    setDirtyCommitteeIds((prev) => {
      const next = new Set(prev);
      if (isDirty) {
        next.add(committeeId);
      } else {
        next.delete(committeeId);
      }
      return next;
    });
  }, []);

  return {
    editingCommitteeId,
    setEditingCommitteeId,
    showSwitchWarningModal,
    setShowSwitchWarningModal,
    handleRequestEdit,
    handleConfirmSwitch,
    handleCancelSwitch,
    handleDirtyChange,
  };
};

export default useCommitteeEdit;
